import { ReactElement } from 'react';
import { Route, Redirect, RouteProps } from 'react-router-dom';

interface ProtectedRouteProps extends RouteProps {
  component: React.ComponentType<any>;
  hasPermission: boolean;
  redirectTo?: string;
}

/**
 * A wrapper around React Router's Route component that conditionally renders
 * based on user permissions. If the user lacks permission, they are redirected
 * to a specified path.
 *
 * @param component - The component to render when user has permission
 * @param hasPermission - Whether the user has permission to access this route
 * @param redirectTo - Path to redirect to when user lacks permission (default: '/')
 * @param rest - Additional props passed to the underlying Route component
 * @returns A Route component that conditionally renders or redirects
 *
 * @example
 * ```tsx
 * <ProtectedRoute
 *   exact
 *   path="/admin"
 *   component={AdminPanel}
 *   hasPermission={user.isAdmin}
 *   redirectTo="/login"
 * />
 * ```
 */
const ProtectedRoute = ({
  component: Component,
  hasPermission,
  redirectTo = '/',
  ...rest
}: ProtectedRouteProps): ReactElement => (
  <Route
    {...rest}
    render={(props) => (hasPermission ? <Component {...props} /> : <Redirect to={redirectTo} />)}
  />
);

export default ProtectedRoute;
